import React, { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from "react-router-dom";
import { Container, Row, Col, Form, Button, Card } from 'react-bootstrap';
import spinnerIcon from './/./../assets/img/loadingSpinner.gif'
import PaginationComponent from '../components/pagination/PaginationComponent.js'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUser, faBuilding, faGear, faContactBook, faXmark, faArrowRight, faEnvelope } from '@fortawesome/free-solid-svg-icons'
import { apiGet } from "../services/AuthService.js";
import { useAuth0 } from "@auth0/auth0-react";

const Account = props => {

    //properties
    const baseUrl = process.env.REACT_APP_API_BASEURL;
    const { getAccessTokenSilently } = useAuth0();

    //stateful properties (hooks)
    const [currentAccount, accountProfile, setAccountProfile, toggleIsShowSidebarMd, setIsShowSidebarMd] = useOutletContext();
    const navigate = useNavigate();
    const [pageStatus, setPageStatus] = useState('loading');
    const [dataStatus, setDataStatus] = useState('loading');
    const [pageErrors, setPageErrors] = useState('');
    const [dataErrors, setDataErrors] = useState('');
    const [accountDetails, setAccountDetails] = useState([]);

    //initialize page
    useEffect(() => {
        (async () => {
            if (setIsShowSidebarMd)
                setIsShowSidebarMd(true);

            await populateAccountDetails()
                .then(response => {
                    if (response.data.length === 0) {
                        console.log(response.data);
                        setDataStatus('nodata')
                    }
                    else {
                        setAccountDetails(response.data);
                        setDataStatus('ready')
                    }

                    setPageStatus('ready')
                })
                .catch(ex => {
                    setPageStatus('error');
                    setPageErrors(ex.message);

                    setDataStatus('error');
                    setDataErrors(ex.message);
                })
                .finally(response => {
                    //do something
                })
        })();
    }, []);

    //get user profile
    const populateAccountDetails = async () => {
        let apiUrl = 'accounts/userprofile';
        console.log(apiUrl);

        const response = await apiGet(apiUrl, getAccessTokenSilently);
        const json = await response.json()
        console.log('Response - Status: ' + response.status + ', Success: ' + response.ok + ', Json: ' + JSON.stringify(json));

        return { success: response.ok, data: json }
    };

    //render page loading
    const showPageLoading = () => {
        return (
            <div>
                <img src={spinnerIcon} alt="loading..." />
            </div>
        );
    }

    //render page error
    const showPageError = () => {
        return (
            <div>
                Oops, there was a page load error: {pageErrors}
            </div>
        );
    }

    const accountCard = (path, icon, label, children, barColor) => (
        <Card className="mb-3" style={{ cursor: 'pointer', background: '#ffffff', border: '1px solid #e5e7eb', borderLeft: `3px solid ${barColor}`, borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,.08)', transition: 'transform 150ms ease, box-shadow 150ms ease' }}
            onClick={() => navigate(path)}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,.14)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,.08)'; }}
        >
            <Card.Header style={{ background: 'transparent', border: 'none', padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                    <div style={{ width: '38px', height: '38px', borderRadius: '9px', background: '#f3f4f6', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' }}>
                        <FontAwesomeIcon icon={icon} style={{ width: '16px', height: '16px', color: barColor }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.82rem', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '.06em', color: '#1f2937', lineHeight: '1.2', marginBottom: '6px' }}>{label}</div>
                        <div style={{ fontSize: '0.82rem', color: '#374151', lineHeight: '1.5' }}>{children}</div>
                    </div>
                    <FontAwesomeIcon icon={faArrowRight} style={{ width: '13px', color: '#9ca3af', flexShrink: 0, marginTop: '4px' }} />
                </div>
            </Card.Header>
        </Card>
    );

    const hasUser = (accountDetails.currentUser ?? '') !== '';
    const hasCompany = (accountDetails.primaryCompany ?? '') !== '';
    const user = hasUser ? accountDetails.currentUser : null;
    const company = hasCompany ? accountDetails.primaryCompany : null;

    //render profile info
    const showProfile = () => {
        return (
            <Container>
                <Row className="justify-content-center">
                    <Col xs={12} sm={10} md={8} lg={6}>
                        {accountCard('/profile', faUser, 'My Profile', hasUser ? (
                            <>
                                <div style={{ fontWeight: '600', color: '#1f2937' }}>{user.firstName} {user.lastName}</div>
                                {user.address1 && <div>{user.address1}</div>}
                                {(user.city || user.state || user.zip) && <div>{user.city}, {user.state} {user.zip}</div>}
                                {user.email && <div>{user.email}</div>}
                                {user.phone && <div>{user.phone}</div>}
                            </>
                        ) : 'View and edit your profile information', '#5B9BD5')}

                        {accountCard('/companies', faBuilding, 'Company Profiles', hasCompany ? (
                            <>
                                <div style={{ fontWeight: '600', color: '#1f2937' }}>Primary Company</div>
                                <div>{company.companyName}</div>
                                {((company.additionalNameInfo ?? '') !== '' && <div>({company.additionalNameInfo})</div>)}
                                {company.address1 && <div>{company.address1}{(company.address2 ?? '') !== '' ? ', ' + company.address2 : ''}</div>}
                                {(company.city || company.state || company.zip) && <div>{company.city}, {company.state} {company.zip}</div>}
                                {company.email && <div>{company.email}</div>}
                                {company.phone && <div>{company.phone}</div>}
                            </>
                        ) : 'View your company profiles', '#5B9BD5')}

                        {accountCard('/settings', faGear, 'Settings', (
                            <div>Owner mapping, CDEX configuration, and more</div>
                        ), '#C8B568')}

                        {accountCard('/contact', faEnvelope, 'Contact', (
                            <>
                                <div style={{ fontWeight: '600', color: '#1f2937' }}>Email</div>
                                <div>support@totdata.com</div>
                            </>
                        ), '#C8B568')}
                    </Col>
                </Row>
            </Container>
        );
    }

    return (
      <div>
            {(() => {
                if (hasCompany) {
                    return <p style={{ fontSize: '1.75rem', fontWeight: '700', color: '#333333', marginBottom: '4px', textAlign: 'center' }}>
                        {accountDetails.primaryCompany.companyName || 'Account'}
                        {((accountDetails.primaryCompany.additionalNameInfo ?? '') !== '' ? ` (${accountDetails.primaryCompany.additionalNameInfo})` : '')}
                    </p>
                }
            })()}

            <p style={{ fontSize: '1.15rem', fontWeight: '500', color: '#6b7280', margin: 0, textAlign: 'center', marginBottom: '20px' }}>Manage your account</p>

            {(() => {
                switch (pageStatus) {
                    case 'loading':
                        return showPageLoading()
                    case 'ready':
                        return showProfile()
                    case 'error':
                        return showPageError()
                    default:
                        return <p>No Page Loaded.</p>
                }
            })()}
      </div>
    );
}

export default Account;
